import React, { useState, useEffect, useCallback, useRef } from 'react'
import Chat, { Bubble, useMessages, Card, Button, CardActions, CardMedia, CardTitle, CardText, Video, Icon } from '@chatui/core'
import '@chatui/core/dist/index.css'
import './UnifiedChatPanel.css'
import WebSocketService, { 
  User, 
  WebSocketMessage, 
  Session, 
  Pagination, 
  SessionsResponse,
  ChatMessage, 
  SessionChatResponse,
  QuickReply
} from '../services/WebSocketService'
import { getAdminUserUrl } from '../utils/adminUrl'

interface UnifiedChatPanelProps {
  onBack?: () => void
}

interface MpImage {
  id?: number | string
  media_id: string
  url: string
  show_url: string
  name: string
  created_at: string
}

const UnifiedChatPanel: React.FC<UnifiedChatPanelProps> = ({ onBack }) => {
  // 用户列表相关状态
  const [users, setUsers] = useState<User[]>([])
  const selectedUserRef = useRef<User | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [hasInitialized, setHasInitialized] = useState(false)


  // 聊天相关状态
  const { messages, appendMsg, resetList, prependMsgs } = useMessages([])
  const [agentName] = useState('客服小美')
  const [chatLoading, setChatLoading] = useState(false)
  const [chatCurrentPage, setChatCurrentPage] = useState(1)
  const chatCurrentPageRef = useRef(1) // 用于跟踪真实的当前页码
  const hasMoreRef = useRef(true) // 用于跟踪hasMore状态，避免异步更新问题
  const [hasMore, setHasMore] = useState(true)
  const [isMobileChatMode, setIsMobileChatMode] = useState(false)
  const [imagePreview, setImagePreview] = useState<{ url: string; visible: boolean }>({ url: '', visible: false })
  const [serverQuickReplies, setServerQuickReplies] = useState<QuickReply[]>([])
  const [appName, setAppName] = useState<string>('dingdang_qrcode')
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(0)

  // 相册相关状态
  const [albumVisible, setAlbumVisible] = useState(false)
  const [mpImages, setMpImages] = useState<MpImage[]>([])
  const [albumLoading, setAlbumLoading] = useState(false)

  // 本地图片上传相关
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [imageUploading, setImageUploading] = useState(false)
  
  // 图片预览工具条状态
  const [imageScale, setImageScale] = useState(1)
  const [qrCodeResult, setQrCodeResult] = useState<string>('')
  const [copySuccess, setCopySuccess] = useState<boolean>(false)

  // 消息已读相关状态
  const [lastMessageId, setLastMessageId] = useState<string>('')

  // 聊天历史加载状态
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false)

  // 搜索相关状态
  const [searchKeyword, setSearchKeyword] = useState('')
  const [appliedSearchKeyword, setAppliedSearchKeyword] = useState('')

  // 快速回复选项
  const quickReplies = [
    {
      icon: 'message',
      name: '联系人工客服',
      isNew: true,
      isHighlight: true,
    },
    {
      name: '常见问题',
      isNew: true,
    },
    {
      name: '订单查询',
      isHighlight: true,
    },
   
  ]

  // 通用刷新用户列表函数
  const refreshUserList = useCallback(() => {
    console.log('刷新用户列表')
    setUsers([])
    setCurrentPage(1)
    setLoading(true)
    WebSocketService.getSessions(1)
  }, [])

  // 加载用户列表（翻页加载）
  const loadSessions = useCallback((page: number, keyword?: string) => {
    console.log('发送sessions请求，页码:', page, '关键词:', keyword)
    setLoading(true)
    setCurrentPage(page)
    WebSocketService.getSessions(page, keyword)
  }, [])

  // 获取聊天历史
  const loadChatHistory = useCallback((userid: string, page: number = 1) => {
    console.log('加载聊天历史，用户ID:', userid, '页码:', page)
    setChatLoading(true)
    setChatCurrentPage(page)
    chatCurrentPageRef.current = page // 立即更新 ref 值
    console.log('setChatCurrentPage:', page, 'ref:', chatCurrentPageRef.current)
    WebSocketService.getSessionChat(userid, page)
  }, [])

  // 获取快捷回复列表
  const loadQuickReplies = useCallback((appName: string) => {
    console.log('获取快捷回复列表，应用名称:', appName)
    WebSocketService.getQuickReplies(appName)
  }, [])

  // 获取素材列表（从 QuickReply 图片类型获取）
  const loadMpImages = useCallback((offset: number = 0, count: number = 10) => {
    console.log('获取素材列表，偏移量:', offset, '数量:', count)
    setAlbumLoading(true)
    WebSocketService.getMpImages(offset, count, appName)
  }, [appName])


  // 触发搜索
  const handleSearch = useCallback(() => {
    const keyword = searchKeyword.trim()
    setAppliedSearchKeyword(keyword)
    loadSessions(1, keyword || undefined)
  }, [searchKeyword, loadSessions])

  // 清空搜索
  const clearSearch = useCallback(() => {
    console.log('清空搜索')
    setSearchKeyword('')
    setAppliedSearchKeyword('')
    loadSessions(1)
  }, [loadSessions])

  // 请求通知权限
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      console.log('通知权限状态:', permission)
      return permission === 'granted'
    }
    return false
  }, [])

  // 发送浏览器通知
  const showNotification = useCallback((title: string, body: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body: body,
        icon: '/favicon.ico', // 可以设置通知图标
        badge: '/favicon.ico',
        tag: 'chat-message', // 使用相同tag会替换之前的通知
        requireInteraction: false, // 通知会自动消失
        silent: false // 允许声音
      })
      
      // 点击通知时聚焦到当前窗口
      notification.onclick = () => {
        window.focus()
        notification.close()
      }
      
      // 3秒后自动关闭通知
      setTimeout(() => {
        notification.close()
      }, 3000)
    }
  }, [])


  // 初始化WebSocket连接和消息监听
  useEffect(() => {
    console.log('UnifiedChatPanel组件初始化')
    
    // 请求通知权限
    requestNotificationPermission()
    
    // 注册组件
    WebSocketService.registerComponent('UnifiedChatPanel')
    
    // 定义消息处理器 - 必须在每次渲染时重新定义
    const handleConnectionChange = (connected: boolean) => {
      console.log('WebSocket连接状态变化:', connected)
      setIsConnected(connected)
    }
    
    const handleMessage = (message: WebSocketMessage) => {
      console.log('收到WebSocket消息:', message)
      
      if (message.type === 'connection_established') {
        console.log('连接建立成功，开始获取sessions')
        refreshUserList()
      } else if (message.type === 'sessions' && message.data) {
        console.log('收到sessions响应:', message.data)
        const sessionsData = message.data as SessionsResponse
        const sessions = sessionsData.sessions
        console.log('分页信息:', sessionsData.pagination)
        console.log('当前页码:', sessionsData.pagination.current_page, '是否有下一页:', sessionsData.pagination.has_next)
        
        const newUsers = sessions.map((session: Session) => ({
          id: session.userid,
          app_name: session.app_name,
          app_name_display: session.app_name_display,
          name: `用户 ${session.userid.slice(-6)}`,
          avatar: '//img.alicdn.com/imgextra/i2/O1CN01fPEB9P1ylYWgaDuVR_!!6000000006619-0-tps-132-132.jpg',
          lastMessage: (session.latest_message as any)?.text ? (session.latest_message as any).text : "[图片]",
          lastMessageTime: new Date(session.latest_message_time).toLocaleTimeString('zh-CN', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          unreadCount: session.user_message_count,
          status: session.status === 'active' ? 'online' : 'offline' as 'online' | 'offline' | 'away'
        }))
        
        // 统一更新用户列表
        setUsers(prev => {
          const isFirstPage = sessionsData.pagination.current_page === 1
          
          if (isFirstPage) {
            console.log('第一页数据，替换用户列表，用户数量:', newUsers.length)
            return newUsers
          } else {
            console.log('非第一页数据，追加用户列表，原有用户数量:', prev.length, '新增用户数量:', newUsers.length, '当前页码:', sessionsData.pagination.current_page)
            return [...prev, ...newUsers]
          }
        })
        
        setPagination(sessionsData.pagination)
        setLoading(false)
        console.log('sessions响应处理完成，重置loading状态')
      } else if (message.type === 'quick_replies' && message.replies) {
        console.log('收到快捷回复响应:', message.replies)
        setServerQuickReplies(message.replies)
      } else if (message.type === 'get_mp_images' && message.data) {
        console.log('收到素材列表响应:', message.data)
        setMpImages((message.data as any).materials)
        setAlbumLoading(false)
      } else if (message.type === 'session_chat' && message.data) {
        console.log('收到聊天历史响应:', message.data)
        const chatData = message.data as SessionChatResponse
        
        // 无论是否有消息，都要清空之前的聊天记录
        if (!chatData.messages || chatData.messages.length === 0) {
          console.log('聊天历史为空，清空聊天记录')
          resetList([])
          setChatLoading(false)
          return
        }
        
        const chatMessages = chatData.messages.map((msg: ChatMessage) => ({
            type: msg.content_type,
            content: msg.content,
            position: msg.message_type === 'user' ? 'left' : 'right' as 'right' | 'left',
            _id: msg.id, // 使用 _id 而不是 id
            createdAt: msg.created_at,
            user: {
              id: msg.userid,
              name: msg.userid,
              avatar: '//img.alicdn.com/imgextra/i2/O1CN01fPEB9P1ylYWgaDuVR_!!6000000006619-0-tps-132-132.jpg',
            },
            
          }))
        
        console.log('处理聊天历史响应，当前页码:', chatCurrentPageRef.current, '消息数量:', chatMessages.length)
        if (chatCurrentPageRef.current === 1) {
          console.log('第一页数据，重置消息列表')
          resetList(chatMessages)
          
          // 标记最后一条消息为已读
          if (chatData.messages && chatData.messages.length > 0) {
            const lastMessage = chatData.messages[chatData.messages.length - 1]
            if (lastMessage && lastMessage.id) {
              console.log('标记最后一条消息为已读，消息ID:', lastMessage.id)
              setLastMessageId(lastMessage.id)
              WebSocketService.markMessageAsRead(lastMessage.id)
            }
          }
        } else {
          console.log('加载更多历史消息，在开头添加消息')
          // 使用 prependMsgs 在开头添加历史消息
          prependMsgs(chatMessages.reverse())
        }
        
        console.log('设置hasMore状态:', chatData.pagination.has_next)
        setHasMore(chatData.pagination.has_next)
        hasMoreRef.current = chatData.pagination.has_next // 同步更新ref
        setChatLoading(false)
        setIsLoadingMoreHistory(false)
    } else if (message.type === 'new_user_message' ) {
          console.log('收到新用户消息:', message)
          const messageData = message.data as any
          
          // 修改页面标题提醒有未读消息
          document.title = '🔴 客服聊天系统'
          
          // 发送浏览器通知
          showNotification('新消息提醒', '您有新的用户消息')
          
          // 检查是否需要刷新用户列表（防抖：5秒内只刷新一次）
          const now = Date.now()
          const shouldRefresh = now - lastRefreshTime > 5000
          
          if (shouldRefresh) {
            console.log('收到新用户消息，刷新用户列表')
            setLastRefreshTime(now)
            refreshUserList()
          } else {
            console.log('收到新用户消息，但距离上次刷新时间过短，跳过刷新')
          }
          
          // 更新现有用户的未读消息数
          setUsers(prev => prev.map(user => 
            user.id === messageData?.userid 
              ? { ...user, unreadCount: user.unreadCount + 1 }
              : user
          ))
          console.log('收到新用户消息，当前状态:', {
            selectedUser: selectedUserRef.current,
            messageUserId: messageData?.userid,
            isSelectedUser: selectedUserRef.current && messageData?.userid === selectedUserRef.current.id
          })
          if (selectedUserRef.current && messageData?.userid === selectedUserRef.current.id) {
            console.log('为当前选中用户添加新消息')
            if(messageData.content_type === 'text'){
            appendMsg({
              type: 'text',
                content: messageData.content,
                position: 'left',
                user: {
                  id: messageData.userid,
                  name: messageData.userid,
                  avatar: '//img.alicdn.com/imgextra/i2/O1CN01fPEB9P1ylYWgaDuVR_!!6000000006619-0-tps-132-132.jpg',
                },
              })
            }else if(messageData.content_type === 'image'){
              appendMsg({
                type: 'image',
                content: messageData.content,
                position: 'left',
                user: {
                  id: messageData.userid,
                  name: messageData.userid,
                  avatar: '//img.alicdn.com/imgextra/i2/O1CN01fPEB9P1ylYWgaDuVR_!!6000000006619-0-tps-132-132.jpg',
                },
              })
            }else if(messageData.content_type === 'video'){
              appendMsg({
                type: 'video',
                content: messageData.content,
                position: 'left',
                user: {
                  id: messageData.userid,
                  name: messageData.userid,
                  avatar: '//img.alicdn.com/imgextra/i2/O1CN01fPEB9P1ylYWgaDuVR_!!6000000006619-0-tps-132-132.jpg',
                },
              })
            } else if(messageData.content_type === 'miniprogram'){
              appendMsg({
                type: 'miniprogram',
                content: messageData.content,
                position: 'left',
                user: {
                  id: messageData.userid,
                  name: messageData.userid,
                  avatar: '//img.alicdn.com/imgextra/i2/O1CN01fPEB9P1ylYWgaDuVR_!!6000000006619-0-tps-132-132.jpg',
                },
              })
            } else if(messageData.content_type === 'ref_docs'){
              appendMsg({
                type: 'ref_docs',
                content: messageData.content,
                position: 'left',
                user: {
                  id: messageData.userid,
                  name: messageData.userid,
                  avatar: '//img.alicdn.com/imgextra/i2/O1CN01fPEB9P1ylYWgaDuVR_!!6000000006619-0-tps-132-132.jpg',
                },
              })
            }
          } else {
            console.log('不是当前选中用户的消息，跳过添加')
          }
      } else if (message.type === 'message_marked_as_read') {
        console.log('消息已标记为已读:', message)
        // 可以在这里添加一些UI反馈，比如显示"已读"状态
        if (message.message_id === lastMessageId) {
          console.log('最后一条消息已成功标记为已读')
        }
      } else if (message.type === 'image_message_sent') {
        console.log('图片消息发送成功:', message)
        setImageUploading(false)
      } else if ((message.type as string) === 'error') {
        console.error('服务端返回错误:', (message as any).message)
        setImageUploading(false)
      }
    }
    
    // 注册消息处理器
    WebSocketService.onConnectionChange(handleConnectionChange)
    WebSocketService.onMessage(handleMessage)
    
    // 只在第一次初始化时连接WebSocket
    if (!hasInitialized) {
      setHasInitialized(true)
      WebSocketService.connect()
    }
    
    return () => {
      WebSocketService.removeConnectionHandler(handleConnectionChange)
      WebSocketService.removeMessageHandler(handleMessage)
      WebSocketService.unregisterComponent('UnifiedChatPanel')
    }
  }, [hasInitialized, requestNotificationPermission, showNotification]) // 只在初始化时执行一次

  // 当选择用户时加载聊天历史
  useEffect(() => {
    console.log('selectedUser useEffect触发，selectedUser:', selectedUserRef.current)
    if (selectedUserRef.current) {
      console.log('用户选择变化，加载聊天历史:', selectedUserRef.current.id)
      setChatLoading(true)
      setChatCurrentPage(1)
      resetList([])
      chatCurrentPageRef.current = 1 // 重置 ref 值
      setIsLoadingMoreHistory(false)
      console.log('切换用户，重置hasMore状态为false')
      setHasMore(false) // 重置hasMore状态，避免上一个用户的状态影响新用户
      hasMoreRef.current = false // 同步更新ref
      WebSocketService.getSessionChat(selectedUserRef.current.id, 1)
      
      const timeout = setTimeout(() => {
        console.log('session_chat请求超时，重置loading状态')
        setChatLoading(false)
      }, 5000)
      
      return () => {
        clearTimeout(timeout)
      }
    } else {
      console.log('selectedUser为null，清空聊天消息')
      resetList([])
      setHasMore(false) // 清空用户时也重置hasMore状态
      hasMoreRef.current = false // 同步更新ref
    }
  }, [selectedUserRef.current, resetList])



  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      // 如果屏幕变大且当前在移动端聊天模式，则退出聊天模式
      if (window.innerWidth > 768 && isMobileChatMode) {
        setIsMobileChatMode(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isMobileChatMode])

  const handleUserSelect = (user: User) => {
    console.log('handleUserSelect被调用，用户:', user)
    
    // 恢复正常的页面标题
    document.title = '客服聊天系统'
    
    // 立即清空聊天记录，避免显示上一个用户的消息
    resetList([])
    
    selectedUserRef.current = user
    console.log('setSelectedUser已调用，用户ID:', user.id)
    
    // 更新appName为用户的app_name
    if (user.app_name) {
      setAppName(user.app_name)
    }
    
    setUsers(prev => prev.map(u => 
      u.id === user.id ? { ...u, unreadCount: 0 } : u
    ))
    
    // 获取快捷回复列表
    loadQuickReplies(user.app_name || appName)
    
    // 在移动端时不需要额外的移动端聊天模式，直接通过CSS控制
    // 移动端聊天模式主要用于桌面端切换到移动端的情况
  }

  // 返回用户列表（移动端）
  const handleBackToList = () => {
    setIsMobileChatMode(false)
    selectedUserRef.current = null
    // 清空聊天记录
    resetList([])
  }

  // 处理图片预览
  const handleImagePreview = useCallback((url: string) => {
    setImagePreview({ url, visible: true })
  }, [])

  // 关闭图片预览
  const handleCloseImagePreview = useCallback(() => {
    setImagePreview({ url: '', visible: false })
    setImageScale(1) // 重置缩放
    setQrCodeResult('') // 清空二维码结果
    setCopySuccess(false) // 重置复制状态
  }, [])
  
  // 放大图片
  const handleZoomIn = useCallback(() => {
    setImageScale(prev => Math.min(prev + 0.5, 3))
  }, [])
  
  // 缩小图片
  const handleZoomOut = useCallback(() => {
    setImageScale(prev => Math.max(prev - 0.5, 0.5))
  }, [])
  
  // 重置缩放
  const handleResetZoom = useCallback(() => {
    setImageScale(1)
  }, [])
  
  // 识别二维码
  const handleScanQRCode = useCallback(async () => {
    if (!imagePreview.url) return
    
    try {
      setQrCodeResult('正在识别二维码...')
      
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (ctx) {
          canvas.width = img.width
          canvas.height = img.height
          ctx.drawImage(img, 0, 0)
          
          // 获取图像数据
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          
          // 尝试使用 jsQR 识别二维码
          try {
            // 动态导入 jsQR
            import('jsqr').then(({ default: jsQR }) => {
              const code = jsQR(imageData.data, imageData.width, imageData.height, {
                inversionAttempts: "dontInvert",
              })
              
              if (code) {
                console.log('识别到二维码:', code.data)
                setQrCodeResult(`二维码内容：${code.data}`)
              } else {
                setQrCodeResult('未识别到二维码，请确保图片中包含清晰的二维码')
              }
            }).catch(() => {
              setQrCodeResult('二维码识别库未安装，请运行 npm install jsqr')
            })
          } catch (error) {
            console.error('jsQR 识别失败:', error)
            setQrCodeResult('二维码识别失败，请确保已安装 jsQR 库')
          }
        }
      }
      
      img.onerror = () => {
        setQrCodeResult('图片加载失败，请检查图片链接')
      }
      
      img.src = imagePreview.url
    } catch (error) {
      console.error('二维码识别失败:', error)
      setQrCodeResult('识别失败，请检查图片是否包含二维码或网络连接')
    }
  }, [imagePreview.url])

  // 复制二维码内容
  const handleCopyQRContent = useCallback(async () => {
    if (!qrCodeResult) return
    
    try {
      // 提取二维码内容（去掉"二维码内容："前缀）
      const content = qrCodeResult.replace('二维码内容：', '').trim()
      
      if (navigator.clipboard && window.isSecureContext) {
        // 现代浏览器使用 Clipboard API
        await navigator.clipboard.writeText(content)
      } else {
        // 兼容旧浏览器
        const textArea = document.createElement('textarea')
        textArea.value = content
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      
      setCopySuccess(true)
      
      // 3秒后重置复制成功状态
      setTimeout(() => {
        setCopySuccess(false)
      }, 3000)
      
    } catch (error) {
      console.error('复制失败:', error)
      // 可以在这里添加错误提示
    }
  }, [qrCodeResult])

  // 处理用户头像点击
  const handleUserAvatarClick = useCallback(() => {
    if (selectedUserRef.current) {
      const url = getAdminUserUrl(selectedUserRef.current.id, selectedUserRef.current.app_name)
      console.log('跳转到用户详情页面:', url)
      window.open(url, '_blank')
    }
  }, [])

  // 处理切换到管理员模式
  const handleSwitchAdmin = useCallback((user: User) => {
    const url = getAdminUserUrl(user.id, user.app_name)
    window.open(url, '_blank')
  }, [])

  // 处理滚动到底部自动加载更多
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const threshold = 150 // 距离底部150px时触发加载，更早触发
    const distanceToBottom = scrollHeight - scrollTop - clientHeight
    
    console.log('滚动事件触发:', {
      scrollTop,
      scrollHeight,
      clientHeight,
      distanceToBottom,
      threshold,
      hasNext: pagination?.has_next,
      loading,
      searchKeyword: appliedSearchKeyword
    })
    
    if (distanceToBottom < threshold) {
      if (pagination && pagination.has_next && !loading) {
        const nextPage = pagination.next_page_number!
        console.log('滚动到底部，自动加载更多用户，下一页:', nextPage, '关键词:', appliedSearchKeyword)
        loadSessions(nextPage, appliedSearchKeyword.trim() || undefined)
      } else {
        console.log('滚动到底部但未触发加载:', {
          hasPagination: !!pagination,
          hasNext: pagination?.has_next,
          loading
        })
      }
    }
  }, [pagination, loading, appliedSearchKeyword, loadSessions])

  const getTextFromContent = (content: any): string => {
    if (!content) return ''
    if (typeof content === 'string') return content
    if (typeof content.text === 'string') return content.text
    if (typeof content.msg === 'string') return content.msg
    return ''
  }

  const proxyWxMediaUrl = (url: string) => {
    return url
      .replace('mmecoa.qpic.cn', 'mmbiz.qpic.cn')
      .replace('https://mmbiz.qpic.cn/', 'http://wx_image_proxy.internal.dd2wm.cn/images/proxy/')
      .replace('http://mmbiz.qpic.cn/', 'http://wx_image_proxy.internal.dd2wm.cn/images/proxy/')
  }

  // 处理表情符号的函数
  const processEmojis = (text: string | undefined | null) => {
    if (text == null || typeof text !== 'string') return ''
    // 表情映射表
    const emojiMap: { [key: string]: string } = {
      '[Facepalm]': '🤦',
      '[Smile]': '😊',
      '[Laugh]': '😄',
      '[Cry]': '😢',
      '[Angry]': '😠',
      '[Surprised]': '😲',
      '[Wink]': '😉',
      '[ThumbsUp]': '👍',
      '[ThumbsDown]': '👎',
      '[Heart]': '❤️',
      '[BrokenHeart]': '💔',
      '[Kiss]': '💋',
      '[Tongue]': '😛',
      '[Cool]': '😎',
      '[Sleepy]': '😴',
      '[Sick]': '🤒',
      '[Party]': '🎉',
      '[Clap]': '👏',
      '[Wave]': '👋',
      '[Pray]': '🙏'
    }
    
    let processedText = text
    // 替换所有表情符号
    Object.keys(emojiMap).forEach(emoji => {
      const regex = new RegExp(emoji.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')
      processedText = processedText.replace(regex, emojiMap[emoji])
    })
    
    return processedText
  }

  // 渲染消息内容
  const renderMessageContent = useCallback((msg: any) => {
    const { type, content, timestamp } = msg
    
    // 格式化时间显示
    const formatTime = (timestamp: string) => {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const now = new Date()
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
      
      if (diffInHours < 24) {
        // 今天：显示时间
        return date.toLocaleTimeString('zh-CN', {
          hour: '2-digit',
          minute: '2-digit'
        })
      } else if (diffInHours < 48) {
        // 昨天：显示"昨天 时间"
        return `昨天 ${date.toLocaleTimeString('zh-CN', {
          hour: '2-digit',
          minute: '2-digit'
        })}`
      } else {
        // 更早：显示完整日期
        return date.toLocaleDateString('zh-CN', {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    }
    
    const timeText = formatTime(timestamp)
    
    switch (type) {
      case 'ref_docs':
        return (
          <div>
            <Bubble>
              <a href={content.ref_docs[0].doc_url} target="_blank">{content.ref_docs[0].doc_name}</a>
            </Bubble>
          </div>
        )
        case 'text': {
          const text = getTextFromContent(content)
          const processedText = processEmojis(text)
          return (
            <div>
              <Bubble content={processedText} />
              {timeText && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#999', 
                  marginTop: '4px',
                  textAlign: msg.position === 'right' ? 'right' : 'left'
                }}>
                  {timeText}
                </div>
              )}
            </div>
          )
        }
      case 'video': {
        const rawVideoUrl = content?.video?.url
        if (!rawVideoUrl) {
          return (
            <div>
              <Bubble content="[视频无法显示]" />
            </div>
          )
        }
        const video_url = proxyWxMediaUrl(rawVideoUrl)
        console.log('content:', video_url)
        return (
          <div>
            <Bubble type="video">
              <Video  src={video_url} controls />
            </Bubble>
            {timeText && (
              <div style={{ 
                fontSize: '12px', 
                color: '#999', 
                marginTop: '4px',
                textAlign: msg.position === 'right' ? 'right' : 'left'
              }}>
                {timeText}
              </div>
            )}
          </div>
        )
      }
      case 'image': {
        const rawPicUrl = content?.image?.url || content?.image?.picurl
        if (!rawPicUrl) {
          return (
            <div>
              <Bubble content="[图片无法显示]" />
            </div>
          )
        }
        const picurl = proxyWxMediaUrl(rawPicUrl)
        console.log('content:', picurl)
        return (
          <div>
            <Bubble type="image">
              <img 
                src={picurl} 
                alt="" 
                style={{ cursor: 'pointer' }}
                onClick={() => handleImagePreview(picurl)}
              />
            </Bubble>
            {timeText && (
              <div style={{ 
                fontSize: '12px', 
                color: '#999', 
                marginTop: '4px',
                textAlign: msg.position === 'right' ? 'right' : 'left'
              }}>
                {timeText}
              </div>
            )}
          </div>
        )
      }
      case 'miniprogram':
        let miniprogrampage = content?.miniprogrampage;
        let textContent = '';
        
        if (!miniprogrampage && content?.multimsg) {
          try {
            for (const msgStr of content.multimsg) {
              if (typeof msgStr === 'string' && msgStr.includes('miniprogrampage')) {
                const parsed = JSON.parse(msgStr);
                miniprogrampage = parsed.miniprogrampage;
              } else if (typeof msgStr === 'string') {
                textContent += msgStr + '\n';
              }
            }
          } catch (e) {
            console.error('Failed to parse miniprogrampage from multimsg', e);
          }
        }
        
        if (!miniprogrampage) {
          return <div>不支持的小程序消息格式</div>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {textContent && (
              <Bubble content={processEmojis(textContent.trim())} />
            )}
            <div>
              <Card fluid size="sm">
                <CardMedia
                  aspectRatio="wide"
                  image={miniprogrampage.thumb_url}
                />
                <CardTitle>{miniprogrampage.title}</CardTitle>
                <CardText>
                  小程序
                </CardText>
                <CardActions>
                  <Button color="primary">查看详情</Button>
                </CardActions>
              </Card>
              {timeText && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#999', 
                  marginTop: '4px',
                  textAlign: msg.position === 'right' ? 'right' : 'left'
                }}>
                  {timeText}
                </div>
              )}
            </div>
          </div>
        )
      default:
        console.warn('未知消息类型:', type)
        return null
    }
  }, [handleImagePreview])

  // 处理小程序卡片点击
  const handleMiniprogramClick = useCallback((miniprogramData: any) => {
    console.log('点击小程序卡片:', miniprogramData)
    // 这里可以添加跳转到小程序的逻辑
    // 例如：window.open(miniprogramData.path) 或调用微信小程序API
  }, [])

  // 手动刷新用户列表
  const handleRefreshUserList = useCallback(() => {
    console.log('手动刷新用户列表')
    setLastRefreshTime(Date.now())
    refreshUserList()
  }, [refreshUserList])

  // 处理快速回复点击
  const handleQuickReplyClick = useCallback((item: any) => {
    // 使用服务器返回的快捷回复内容
    const replyContent = serverQuickReplies.find(reply => reply.name === item.name)?.content || item.name
    console.log('replyContent:', replyContent,)
    var content_type = "text"
    if(replyContent.text){
      appendMsg({
        type: 'text',
        content: {text: replyContent.text},
        position: 'right',
      })
      content_type = "text"
    }else if(replyContent.image){
      appendMsg({
        type: 'image',
        content: {image: replyContent.image},
        position: 'right',
      })
      content_type = "image"
    } else if(replyContent.miniprogrampage){
      appendMsg({
        type: 'miniprogram',
        content: {miniprogrampage: replyContent.miniprogrampage},
        position: 'right',
      })
      content_type = "miniprogram"
    }
    if (selectedUserRef.current) {
      const message: WebSocketMessage = {
        type: 'customer_service_message',
        userid: selectedUserRef.current.id,
        message: replyContent,
        content_type: content_type,
        timestamp: new Date().toISOString()
      }
      console.log('发送快速回复消息:', message)
      WebSocketService.send(message)
    }
  }, [appendMsg, serverQuickReplies])

  // 打开相册
  const handleOpenAlbum = useCallback(() => {
    console.log('打开相册')
    setAlbumVisible(true)
    loadMpImages()
  }, [loadMpImages])

  // 关闭相册
  const handleCloseAlbum = useCallback(() => {
    console.log('关闭相册')
    setAlbumVisible(false)
  }, [])

  // 选择图片
  const handleSelectImage = useCallback((image: MpImage) => {
    console.log('选择图片:', image)
    
    // 发送图片消息
    appendMsg({
      type: 'image',
      content: {image: {url: image.show_url, media_id: image.media_id}},
      position: 'right',
    })

    if (selectedUserRef.current) {
      const message: WebSocketMessage = {
        type: 'customer_service_message',
        userid: selectedUserRef.current.id,
        message: {image: {url: image.url}},
        content_type: 'image',
        timestamp: new Date().toISOString()
      }
      console.log('发送图片消息:', message)
      WebSocketService.send(message)
    }

    // 关闭相册
    setAlbumVisible(false)
  }, [appendMsg])

  // 将图片文件压缩为 JPEG dataURL，控制体积，避免 WebSocket 传输过大
  const compressImageToDataUrl = useCallback((file: File, maxSize = 1280, quality = 0.8): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const img = new Image()
        img.onload = () => {
          let { width, height } = img
          if (width > maxSize || height > maxSize) {
            if (width >= height) {
              height = Math.round((height * maxSize) / width)
              width = maxSize
            } else {
              width = Math.round((width * maxSize) / height)
              height = maxSize
            }
          }
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('无法创建画布上下文'))
            return
          }
          // 透明背景统一填白，避免 JPEG 透明区域变黑
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, width, height)
          ctx.drawImage(img, 0, 0, width, height)
          resolve(canvas.toDataURL('image/jpeg', quality))
        }
        img.onerror = () => reject(new Error('图片解码失败'))
        img.src = reader.result as string
      }
      reader.onerror = () => reject(new Error('图片读取失败'))
      reader.readAsDataURL(file)
    })
  }, [])

  // 点击「图片」工具按钮，唤起本地文件选择
  const handlePickImage = useCallback(() => {
    if (!selectedUserRef.current) {
      console.warn('未选择用户，无法发送图片')
      return
    }
    fileInputRef.current?.click()
  }, [])

  // 选择本地图片后：压缩 -> 乐观上屏 -> 经 WebSocket 上传发送
  const handleImageFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // 立即清空，允许再次选择同一文件
    e.target.value = ''
    if (!file) return

    if (!selectedUserRef.current) {
      console.warn('未选择用户，忽略图片')
      return
    }
    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('图片不能超过 10MB')
      return
    }

    try {
      setImageUploading(true)
      const dataUrl = await compressImageToDataUrl(file)

      // 乐观显示：先在客服侧把图片渲染出来
      appendMsg({
        type: 'image',
        content: { image: { url: dataUrl } },
        position: 'right',
      })

      // 通过 WebSocket 上传，后端换取 media_id 后下发给用户
      WebSocketService.sendImageMessage(selectedUserRef.current.id, dataUrl)
    } catch (err) {
      console.error('图片处理失败:', err)
      alert('图片处理失败，请重试')
    } finally {
      setImageUploading(false)
    }
  }, [appendMsg, compressImageToDataUrl])

  // 处理聊天区域的滚动事件（带防抖）
  
  const handleChatScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
 
    console.log('hasMore:', hasMore, 'hasMoreRef:', hasMoreRef.current)
    // 当滚动到顶部且有更多历史消息时，加载更多
    if (scrollTop == 0 && hasMoreRef.current && !chatLoading && !isLoadingMoreHistory && selectedUserRef.current) {
      const nextPage = chatCurrentPage + 1
      console.log('滚动到顶部，自动加载更多历史消息，下一页:', nextPage)
      setIsLoadingMoreHistory(true)
      loadChatHistory(selectedUserRef.current.id, nextPage)
    }
  }
  
  return (
    <div className={`unified-chat-panel ${isMobileChatMode ? 'mobile-chat-mode' : ''} ${selectedUserRef.current ? 'has-selected-user' : ''}`}>
      {/* 本地图片选择（隐藏，由工具栏「图片」按钮唤起） */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageFileChange}
      />

      {/* 上传中提示 */}
      {imageUploading && (
        <div className="image-uploading-tip">图片发送中...</div>
      )}

      {/* 用户列表侧边栏 */}
      <div className={`user-list-sidebar ${isMobileChatMode ? 'hidden' : ''}`}>
        <div className="user-list">
          <div className="user-list-header">
            <h3>用户列表</h3>
            <div className="connection-status">
              <span className={`status-dot ${isConnected ? 'online' : 'offline'}`}></span>
              {isConnected ? '已连接' : '未连接'}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button 
                onClick={handleRefreshUserList} 
                disabled={loading}
                style={{
                  background: '#52c41a',
                  color: 'white',
                  border: 'none',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                刷新列表
              </button>
            </div>
            
            {/* 搜索框 */}
       
          </div>
          <div className="search-container">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="搜索用户..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch()
                    }
                  }}
                  className="search-input"
                />
                {searchKeyword && (
                  <button
                    onClick={clearSearch}
                    className="search-clear-btn"
                    title="清空搜索"
                  >
                    ×
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={handleSearch}
                className="search-submit-btn"
                disabled={loading}
              >
                搜索
              </button>
            </div>
          <div className="user-list-content" onScroll={handleScroll}>
            {loading && currentPage === 1 && (
              <div className="loading">加载中...</div>
            )}
            {users.map(user => (
              <div
                key={user.id}
                className={`user-item ${selectedUserRef.current?.id === user.id ? 'active' : ''}`}
                onClick={() => {
                  console.log('用户点击事件触发，用户:', user)
                  handleUserSelect(user)
                }}
              >
                <div className="user-avatar">
                  <img src={user.avatar} alt={String(user.name || '')} />
                  <span className={`status-indicator ${user.status}`}></span>
                </div>
                <div className="user-info">
                  <div className="user-name">{String(user.name || '')}</div>
                  <div className="user-last-message">{String(user.lastMessage || '')}</div>
                  <div className="user-meta">
                    <span className="last-time">{String(user.lastMessageTime || '')}</span>
                    {user.unreadCount > 0 && (
                      <span className="unread-count">{user.unreadCount}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}

          </div>
        </div>
      </div>

      {/* 聊天主区域 */}
      <div className={`chat-main-area ${isMobileChatMode ? 'fullscreen' : ''}`}>
        {!selectedUserRef.current ? (
          <div className="no-selection">
            <div className="no-selection-content">
              <h3>请选择用户开始对话</h3>
              <p>从左侧用户列表中选择一个用户开始客服对话</p>
            </div>
          </div>
        ) : (
          <div className="enhanced-chat">
            {chatLoading && (
              <div className="chat-loading">
                <div className="loading-message">正在加载聊天记录...</div>
              </div>
            )}
            <Chat
              navbar={{
                title: selectedUserRef.current!.name,
                logo: selectedUserRef.current!.avatar,
                desc: selectedUserRef.current!.app_name_display,
                align: "left",
                leftContent: {
                  icon: 'chevron-left',
                  onClick: handleBackToList,
                },
                rightContent: [
                  {
                    icon: 'apps',
                    onClick: () => {
                      handleSwitchAdmin(selectedUserRef.current!)
                    },
                  }
                ]
              }}
              messages={messages}
              renderMessageContent={renderMessageContent}
              quickReplies={serverQuickReplies.length > 0 ? serverQuickReplies.map(reply => ({
                name: reply.name,
                isHighlight: reply.use_count > 0
              })) : quickReplies}
              onQuickReplyClick={handleQuickReplyClick}
              onScroll={handleChatScroll}
              onSend={(type: string, val: string) => {
                if (type === 'text' && val.trim()) {
                  appendMsg({
                    type: 'text',
                    content: { text: val },
                    position: 'right',
                  })

                  if (selectedUserRef.current) {
                    const message: WebSocketMessage = {
                      type: 'customer_service_message',
                      userid: selectedUserRef.current.id,
                      message: {text: val},
                      timestamp: new Date().toISOString()
                    }
                    console.log('发送客服消息:', message)
                    WebSocketService.send(message)
                  }
                }
              }}
              onToolbarClick={(item: any) => {
                if (item.type === 'album') {
                  handleOpenAlbum()
                } else if (item.type === 'image') {
                  handlePickImage()
                }
              }}
              locale="zh-CN"
              placeholder="请输入回复内容..."
              toolbar={[
                {
                  type: 'image',
                  icon: 'image',
                  title: '图片',
                },
                {
                  type: 'album',
                  icon: 'image',
                  title: '相册',
                },
              ]}
            />
          </div>
        )}
      </div>
      
      {/* 图片预览组件 */}
      {imagePreview.visible && (
        <div className="image-preview-overlay" onClick={handleCloseImagePreview}>
          <div className="image-preview-container" onClick={(e) => e.stopPropagation()}>
            <button className="image-preview-close" onClick={handleCloseImagePreview}>
              ×
            </button>
            <div className="image-preview-content">
              <img 
                src={imagePreview.url} 
                alt="预览图片" 
                className="image-preview-image" 
                style={{ transform: `scale(${imageScale})` }}
              />
            </div>
            
            {/* 图片预览工具条 */}
            <div className="image-preview-toolbar">
              <div className="toolbar-section">
                <button 
                  className="toolbar-btn" 
                  onClick={handleZoomOut}
                  disabled={imageScale <= 0.5}
                  title="缩小"
                >
                  <Icon type="minus" />
                </button>
                <span className="scale-text">{Math.round(imageScale * 100)}%</span>
                <button 
                  className="toolbar-btn" 
                  onClick={handleZoomIn}
                  disabled={imageScale >= 3}
                  title="放大"
                >
                  <Icon type="plus" />
                </button>
                <button 
                  className="toolbar-btn" 
                  onClick={handleResetZoom}
                  title="重置缩放"
                >
                  <Icon type="refresh" />
                </button>
              </div>
              
              <div className="toolbar-section">
                <button 
                  className="toolbar-btn qr-btn" 
                  onClick={handleScanQRCode}
                  title="识别二维码"
                >
                  <Icon type="apps" />
                </button>
              </div>
            </div>
            
            {/* 二维码识别结果 */}
            {qrCodeResult && (
              <div className="qr-result">
                <div className="qr-result-header">
                  <span>二维码内容：</span>
                  <div className="qr-result-actions">
                    <button 
                      className="qr-copy-btn" 
                      onClick={handleCopyQRContent}
                      disabled={!qrCodeResult.includes('二维码内容：')}
                      title="复制内容"
                    >
                      {copySuccess ? '已复制' : '复制'}
                    </button>
                    <button 
                      className="qr-close-btn" 
                      onClick={() => setQrCodeResult('')}
                      title="关闭"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div 
                  className="qr-result-content"
                  onClick={qrCodeResult.includes('二维码内容：') ? handleCopyQRContent : undefined}
                  style={{ 
                    cursor: qrCodeResult.includes('二维码内容：') ? 'pointer' : 'default' 
                  }}
                  title={qrCodeResult.includes('二维码内容：') ? '点击复制' : ''}
                >
                  {qrCodeResult}
                  {copySuccess && (
                    <div className="copy-success-indicator">
                      <Icon type="check" />
                      已复制到剪贴板
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 相册弹窗 */}
      {albumVisible && (
        <div className="album-overlay" onClick={handleCloseAlbum}>
          <div className="album-container" onClick={(e) => e.stopPropagation()}>
            <div className="album-header">
              <h3>素材库</h3>
              <button className="album-close" onClick={handleCloseAlbum}>
                ×
              </button>
            </div>
            <div className="album-content">
              {albumLoading ? (
                <div className="album-loading">加载中...</div>
              ) : mpImages.length > 0 ? (
                <div className="album-grid">
                  {mpImages.map((image, index) => (
                    <div 
                      key={image.id ?? index} 
                      className="album-item"
                      onClick={() => handleSelectImage(image)}
                    >
                      <img src={image.show_url} alt={image.name} />
                      <div className="album-item-name">{image.name}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="album-empty">暂无素材</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UnifiedChatPanel 